// pages/AdminBoard.jsx — Main Admin Shell
// Route: /admin  (protected — admin role required)
// Tabs: Overview · Plans · Firms · Support Log · System
//
// FIX (July 2026): this file was never actually converted to the platform's
// dark theme despite being marked done — it was still 95 raw Tailwind
// light-mode classes (bg-white, text-gray-900, bg-gray-50, etc.), which is
// why it visually clashed with the rest of the (dark) app. Converted to the
// same T-token inline-style pattern used in FirmManagement.jsx,
// PlanManagement.jsx, and SystemSettings.jsx for consistency.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminStats, adminListFirms, getSupportLog, addSupportNote, getEscalations, resolveEscalation, getPracticeNumberQueue, getStatutoryRates, getSbcTablesCoverage, mintAdminSsoToken } from '../api/kailux';
import PlanManagement from './PlanManagement';
import FirmManagement from './FirmManagement';
import SystemSettings from './SystemSettings';
import StatusMaintenance from './StatusMaintenance';
import AdminUsers from './AdminUsers';
import AdminSettings from './AdminSettings';
import PracticeNumberQueue from './PracticeNumberQueue';
import StatutoryRates from './StatutoryRates';
import SbcTaxTables, { CoverageBanner } from './SbcTaxTables';
import AuditAdmin from './AuditAdmin';
import { LayoutDashboard, Bell, CreditCard, Building2, Archive, Settings as SettingsIcon, ShieldCheck, Percent, Table2, User, LogOut, Activity } from 'lucide-react';
import EmptyState from '../components/EmptyState';
import Spinner from '../components/Spinner'
import CollapsibleSidebar from '../components/CollapsibleSidebar'

import Icon, { ICONS } from '../components/Icon'

import { T, alpha } from '../theme/theme'
const inp = {
  width:'100%', padding:'8px 12px', background:T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color:T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif", boxSizing:'border-box',
}

// ─── Stat Card ────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'20px' }}>
      <p style={{ fontSize:'12px', color:T.lt, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>{label}</p>
      <p
        style={{ fontSize:'28px', marginTop:'4px', color: accent || T.tx, fontFamily:"'DM Mono', monospace", fontWeight:700 }}
      >
        {value}
      </p>
      {sub && <p style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>{sub}</p>}
    </div>
  );
}

// ─── Home Tab ─────────────────────────────────────────────────
function AdminHome() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminStats()
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ height:'256px', display:'flex', alignItems:'center', justifyContent:'center', color:T.mt }}>Loading stats…</div>
  );

  if (!stats) return (
    <div style={{ color:T.rd, fontSize:'14px' }}>Failed to load stats.</div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'32px' }}>
      <div>
        <h2 style={{ fontSize:'20px', color:T.tx, fontFamily:"'Syne', sans-serif", fontWeight:800 }}>Platform Overview</h2>
        <p style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginTop:'2px' }}>Live snapshot across all KaiLux firms.</p>
      </div>

      {/* Primary stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' }}>
        <StatCard label="Total Firms"    value={stats.total_firms}    sub={`+${stats.new_firms_30d} last 30 days`} />
        <StatCard label="Active"         value={stats.active_firms}   accent={T.gn} />
        <StatCard label="Unpaid"         value={stats.unpaid_firms}   accent={T.bl} />
      </div>

      {/* AFS stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' }}>
        <StatCard label="Total AFS Jobs"   value={stats.total_afs_jobs}  />
        <StatCard label="Completed Jobs"   value={stats.completed_jobs}  accent={T.gn} />
        <StatCard label="Jobs — 30 Days"   value={stats.jobs_30d}        sub="generated this month" />
      </div>

      {/* Seat breakdown */}
      <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'20px' }}>
        <h3 style={{ color:T.tx, marginBottom:'16px', fontFamily:"'Syne', sans-serif", fontWeight:800, fontSize:'15px' }}>Firms by Team Size</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {[
            { label: '10+ users',  key: '10_plus', color: T.pu },
            { label: '5–9 users',  key: '5_9',      color: T.bl },
            { label: '2–4 users',  key: '2_4',      color: T.gn },
            { label: '1 user',     key: '1',        color: T.mt },
          ].map(({ label, key, color }) => {
            const count = stats.seat_breakdown[key] || 0;
            const pct   = stats.total_firms > 0 ? Math.round((count / stats.total_firms) * 100) : 0;
            return (
              <div key={key} style={{ display:'flex', alignItems:'center', gap:'16px' }}>
                <span style={{ width:'96px', fontSize:'14px', color:T.lt }}>{label}</span>
                <div style={{ flex:1, background:T.s2, borderRadius:'999px', height:'10px', overflow:'hidden' }}>
                  <div style={{ height:'100%', borderRadius:'999px', width:`${pct}%`, background:color }} />
                </div>
                <span style={{ width:'64px', textAlign:'right', fontSize:'14px', color:T.lt }}>{count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick links */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' }}>
        {[
          {
            title: 'Pricing out of date?',
            desc:  'Update seat pricing and overage rates in Plan Management — live in seconds.',
            action: 'Go to Plans →',
            tab: 'plans',
          },
          {
            title: 'Firm needs help?',
            desc:  'Extend their trial, reset usage, or add a support note.',
            action: 'Go to Firms →',
            tab: 'firms',
          },
          {
            title: 'Roll out a new feature?',
            desc:  "Flip the feature flag ON when it's ready for all firms.",
            action: 'Go to System →',
            tab: 'system',
          },
        ].map(card => (
          <div key={card.tab} style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
            <p style={{ fontWeight:600, color:T.tx, fontSize:'14px' }}>{card.title}</p>
            <p style={{ fontSize:'13px', color:T.lt, marginTop:'4px', marginBottom:'12px' }}>{card.desc}</p>
            <span style={{ fontSize:'13px', fontWeight:600, color:T.or }}>{card.action}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Escalations Tab (operator inbox — fed by Help Chat) ───────
function Escalations({ onResolved }) {
  const [entries, setEntries] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingId, setResolvingId] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = (unreadOnly) => {
    setLoadingList(true);
    setLoadError('');
    getEscalations(unreadOnly)
      .then(d => setEntries(d.entries || []))
      .catch(() => setLoadError('Failed to load escalations.'))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => { load(!showResolved); }, [showResolved]);

  const markResolved = async (id) => {
    setResolvingId(id);
    try {
      await resolveEscalation(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      onResolved?.();
    } catch {
      setLoadError('Failed to mark as resolved. Please try again.');
    } finally {
      setResolvingId('');
    }
  };

  const timeAgo = (iso) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div>
      <div style={{ marginBottom:'24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h2 style={{ fontSize:'20px', color:T.tx, fontFamily:"'Syne', sans-serif", fontWeight:800 }}>Escalations</h2>
          <p style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginTop:'2px' }}>
            Requests from the Help Chat where a firm asked to speak with a person.
          </p>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px', color:T.lt, cursor:'pointer', userSelect:'none' }}>
          <input
            type="checkbox"
            checked={showResolved}
            onChange={e => setShowResolved(e.target.checked)}
          />
          Show resolved too
        </label>
      </div>

      {loadError && (
        <div style={{ marginBottom:'16px', fontSize:'14px', color:T.rd, background:T.rdd, border:`1px solid ${T.rd}`, borderRadius:'8px', padding:'8px 12px' }}>
          {loadError}
        </div>
      )}

      {loadingList ? (
        <div style={{ height:'192px', display:'flex', alignItems:'center', justifyContent:'center' }}><Spinner/></div>
      ) : entries.length === 0 ? (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'40px', textAlign:'center' }}>
          <div style={{ marginBottom:'8px', display:'flex', justifyContent:'center' }}><Bell size={26} strokeWidth={1.5} color={T.lt}/></div>
          <p style={{ fontSize:'14px', color:T.lt }}>
            {showResolved ? 'No escalations yet.' : "No open escalations — you're all caught up."}
          </p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {entries.map(e => (
            <div key={e.id} style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'16px' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                    <p style={{ fontSize:'14px', fontWeight:600, color:T.tx }}>
                      {e.firms?.name || 'Unknown firm'}
                    </p>
                    <span style={{ fontSize:'12px', color:T.mt }}>{timeAgo(e.created_at)}</span>
                    {!e.is_read && (
                      <span style={{ fontSize:'12px', fontWeight:700, color:T.rd, background:T.rdd, borderRadius:'999px', padding:'2px 8px' }}>
                        New
                      </span>
                    )}
                  </div>
                  {e.firms?.email && (
                    <p style={{ fontSize:'12px', color:T.mt, marginBottom:'8px' }}>{e.firms.email}</p>
                  )}
                  <p style={{ fontSize:'14px', color:T.lt, whiteSpace:'pre-wrap' }}>{e.note}</p>
                </div>
                {!e.is_read && (
                  <button
                    onClick={() => markResolved(e.id)}
                    disabled={resolvingId === e.id}
                    style={{ flexShrink:0, fontSize:'12px', fontWeight:600, padding:'6px 12px', borderRadius:'8px',
                      border:`1px solid ${T.bd}`, background:'transparent', color:T.lt,
                      cursor:'pointer', opacity: resolvingId === e.id ? 0.5 : 1 }}
                  >
                    {resolvingId === e.id ? 'Saving…' : 'Mark resolved'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Support Log Tab ───────────────────────────────────────────
function SupportLog() {
  const [firms,          setFirms]          = useState([]);
  const [loadingFirms,   setLoadingFirms]   = useState(true);
  const [search,         setSearch]         = useState('');
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [log,            setLog]            = useState([]);
  const [loadingLog,     setLoadingLog]     = useState(false);
  const [newNote,        setNewNote]        = useState('');
  const [saving,         setSaving]         = useState(false);
  const [saveError,      setSaveError]      = useState('');

  // Load all firms for the selector
  useEffect(() => {
    adminListFirms()
      .then(data => {
        // API may return { firms: [...] } or directly an array
        setFirms(Array.isArray(data) ? data : (data.firms || []));
        setLoadingFirms(false);
      })
      .catch(() => setLoadingFirms(false));
  }, []);

  // Load the support log for a selected firm
  const loadLog = (firmId) => {
    setSelectedFirmId(firmId);
    setLog([]);
    if (!firmId) return;
    setLoadingLog(true);
    getSupportLog(firmId)
      .then(data => {
        setLog(Array.isArray(data) ? data : (data.log || []));
      })
      .catch(() => setLog([]))
      .finally(() => setLoadingLog(false));
  };

  // Submit a new support note
  const submitNote = async () => {
    if (!newNote.trim() || !selectedFirmId) return;
    setSaving(true);
    setSaveError('');
    try {
      await addSupportNote(selectedFirmId, newNote.trim());
      setNewNote('');
      loadLog(selectedFirmId); // refresh the log
    } catch (e) {
      setSaveError('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filteredFirms = firms.filter(f =>
    (f.name  || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedFirm = firms.find(f => f.id === selectedFirmId);

  return (
    <div>
      <div style={{ marginBottom:'24px' }}>
        <h2 style={{ fontSize:'20px', color:T.tx, fontFamily:"'Syne', sans-serif", fontWeight:800 }}>Support Log</h2>
        <p style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginTop:'2px' }}>
          View and add support notes per firm. Notes are internal — firms cannot see them.
        </p>
      </div>

      <div style={{ display:'flex', gap:'20px', height:'calc(100vh - 240px)', minHeight:'480px' }}>

        {/* ── Firm selector ── */}
        <div style={{ width:'288px', background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
          display:'flex', flexDirection:'column', overflow:'hidden', flexShrink:0 }}>
          <div style={{ padding:'16px', borderBottom:`1px solid ${T.bd}` }}>
            <p style={{ fontSize:'12px', fontWeight:600, color:T.lt, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px' }}>
              {loadingFirms ? 'Loading…' : `${firms.length} firms`}
            </p>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={inp}
            />
          </div>

          <div style={{ flex:1, overflowY:'auto' }}>
            {loadingFirms ? (
              <div style={{ padding:'16px', fontSize:'14px', color:T.mt }}>Loading firms…</div>
            ) : filteredFirms.length === 0 ? (
              <EmptyState icon={ICONS.building} title="No firms found" size="compact" />
            ) : filteredFirms.map(f => {
              const isSelected = selectedFirmId === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => loadLog(f.id)}
                  style={{ width:'100%', textAlign:'left', padding:'12px 16px',
                    border:'none', borderBottom:`1px solid ${T.bd}`,
                    background:isSelected ? T.prg : 'transparent', cursor:'pointer' }}
                >
                  <div style={{ fontSize:'14px', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                    color: isSelected ? T.onpri : T.tx }}>
                    {f.name || '—'}
                  </div>
                  <div style={{ fontSize:'12px', marginTop:'2px', color: isSelected ? alpha(T.tx, 0.75) : T.mt }}>
                    {f.subscription_status || 'unknown'} · {f.plan_name || 'Standard'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Log panel ── */}
        <div style={{ flex:1, background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
          display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {!selectedFirmId ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:T.mt, gap:'8px' }}>
              <span style={{ fontSize:'30px' }}><Icon name="folder" strokeWidth={1.5} style={{ color: T.mt }} /></span>
              <p style={{ fontSize:'14px' }}>Select a firm to view their support log</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding:'16px', borderBottom:`1px solid ${T.bd}`, display:'flex',
                alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                <div>
                  <p style={{ fontWeight:600, color:T.tx, fontSize:'14px' }}>{selectedFirm?.name || selectedFirmId}</p>
                  <p style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                    {selectedFirm?.email || ''} · {selectedFirm?.subscription_status || ''}
                  </p>
                </div>
                <span style={{ fontSize:'12px', background:T.s2, color:T.lt, padding:'4px 12px', borderRadius:'999px', fontWeight:600 }}>
                  {log.length} {log.length === 1 ? 'entry' : 'entries'}
                </span>
              </div>

              {/* Log entries — newest first */}
              <div style={{ flex:1, overflowY:'auto', padding:'16px', display:'flex', flexDirection:'column', gap:'12px' }}>
                {loadingLog ? (
                  <Spinner fontSize='14px'/>
                ) : log.length === 0 ? (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'128px', color:T.mt, gap:'4px' }}>
                    <p style={{ fontSize:'14px' }}>No support notes for this firm yet.</p>
                    <p style={{ fontSize:'12px' }}>Add the first note below.</p>
                  </div>
                ) : (
                  [...log].reverse().map((entry, i) => (
                    <div key={i} style={{ border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                        <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:'24px', height:'24px',
                          borderRadius:'999px', background:T.s2, color:T.lt, fontSize:'12px', fontWeight:700 }}>
                          {(entry.created_by || 'A')[0].toUpperCase()}
                        </span>
                        <span style={{ fontSize:'12px', fontWeight:600, color:T.lt }}>
                          {entry.created_by || 'Admin'}
                        </span>
                        <span style={{ color:T.bd }}>·</span>
                        <span style={{ fontSize:'12px', color:T.mt }}>
                          {entry.created_at
                            ? new Date(entry.created_at).toLocaleString('en-ZA', {
                                day: 'numeric', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })
                            : 'Unknown date'}
                        </span>
                      </div>
                      <p style={{ fontSize:'14px', color:T.tx, whiteSpace:'pre-wrap', lineHeight:1.6 }}>
                        {entry.note}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Add note form */}
              <div style={{ borderTop:`1px solid ${T.bd}`, padding:'16px', flexShrink:0 }}>
                <p style={{ fontSize:'12px', fontWeight:600, color:T.lt, marginBottom:'8px' }}>Add a note</p>
                <textarea
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  placeholder="e.g. Called firm about their unpaid balance — asked to add 3 more seats. Follow up Friday."
                  rows={3}
                  style={{ ...inp, resize:'none' }}
                  onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) submitNote(); }}
                />
                {saveError && (
                  <p style={{ fontSize:'12px', color:T.rd, marginTop:'4px' }}>{saveError}</p>
                )}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'8px' }}>
                  <span style={{ fontSize:'12px', color:T.mt }}>Ctrl+Enter to save</span>
                  <button
                    onClick={submitNote}
                    disabled={saving || !newNote.trim()}
                    style={{ padding:'8px 16px', background:T.prg, color:T.onpri, fontSize:'14px', fontWeight:600,
                      border:'none', borderRadius:'8px', cursor: (saving || !newNote.trim()) ? 'not-allowed' : 'pointer',
                      opacity: (saving || !newNote.trim()) ? 0.4 : 1 }}
                  >
                    {saving ? 'Saving…' : 'Add Note'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Nav Item ─────────────────────────────────────────────────
function NavItem({ icon: Icon, label, active, onClick, badge }) {
  return (
    <button
      onClick={onClick}
      style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px',
        borderRadius:'12px', fontSize:'14px', fontWeight:600, transition:'all 0.15s', border:'none',
        cursor:'pointer', background:active ? T.prg : 'transparent', color: active ? T.onpri : T.lt }}
    >
      <Icon size={16} strokeWidth={2} style={{ flexShrink:0 }} />
      <span style={{ flex:1, textAlign:'left' }}>{label}</span>
      {badge > 0 && (
        <span style={{ fontSize:'12px', fontWeight:700, borderRadius:'999px', padding:'2px 6px', minWidth:'20px',
          textAlign:'center', background: active ? alpha(T.onpri, 0.25) : T.rd, color:T.onpri }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

// ─── Main AdminBoard ──────────────────────────────────────────
const TABS = [
  { key: 'home',        label: 'Overview',     icon: LayoutDashboard },
  { key: 'escalations', label: 'Escalations',  icon: Bell },
  { key: 'practice-numbers', label: 'Practice Numbers', icon: ShieldCheck },
  { key: 'statutory-rates', label: 'Statutory Rates', icon: Percent },
  { key: 'sbc-tables', label: 'SBC Tax Tables', icon: Table2 },
  { key: 'audit', label: 'Audit', icon: ShieldCheck },
  { key: 'plans',       label: 'Plans',        icon: CreditCard },
  { key: 'firms',       label: 'Firms',        icon: Building2 },
  { key: 'support',     label: 'Support Log',  icon: Archive },
  { key: 'system',      label: 'System',       icon: SettingsIcon },
  { key: 'status',      label: 'Status Page',  icon: Activity },
  { key: 'admins',      label: 'Admin Users',  icon: ShieldCheck },
  { key: 'account',     label: 'My Account',   icon: User },
];

// admin.kailux.co.za only ever renders AdminApp (login + this board) — there
// is no /dashboard to fall back to there, unlike on the client app domains.
const isAdminHost = typeof window !== 'undefined' && window.location.hostname.startsWith('admin.');

export default function AdminBoard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]         = useState('home');
  // Sidebar uses position:fixed (see <aside> below), so unlike the client
  // app's sticky sidebar, collapsing it doesn't automatically reflow <main>
  // — that margin has to be animated in step, hence lifting the state here
  // via CollapsibleSidebar's onCollapsedChange instead of letting it fully
  // self-manage.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin]   = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pnPendingCount, setPnPendingCount] = useState(0);
  const [srPendingCount, setSrPendingCount] = useState(0);
  const [sbcCoverage, setSbcCoverage] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState('');

  // "Open Client App" — mints a short-lived, single-use SSO token and opens
  // the client app's /sso landing page with it, rather than a bare
  // (logged-out) link. See the comment above the button itself for the
  // full rationale/history.
  const handleOpenClientApp = async () => {
    setSsoLoading(true); setSsoError('');
    try {
      const { token } = await mintAdminSsoToken();
      const clientAppUrl = isAdminHost
        ? `https://${window.location.hostname.replace(/^admin\./, 'app.')}`
        : window.location.origin;
      window.open(`${clientAppUrl}/sso?token=${encodeURIComponent(token)}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setSsoError(err.response?.data?.detail || 'Could not open the client app. Try again.');
    } finally {
      setSsoLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    const meta = user.app_metadata || {};
    if (meta.role !== 'admin') {
      navigate(isAdminHost ? '/login' : '/dashboard');
      return;
    }
    setIsAdmin(true);
    setChecking(false);
  }, [user, loading, navigate]);

  // Poll for new escalations every 60s so the sidebar badge stays current
  // without the admin needing to open the Escalations tab.
  useEffect(() => {
    if (!isAdmin) return;
    const refresh = () => {
      getEscalations(true)
        .then(d => setUnreadCount((d.entries || []).length))
        .catch(() => {});
      getPracticeNumberQueue('pending')
        .then(d => setPnPendingCount((d.entries || []).length))
        .catch(() => {});
      getStatutoryRates()
        .then(d => setSrPendingCount((d.pending || []).length))
        .catch(() => {});
      // SBC tax-table coverage alert (§104): warns before the latest table runs out.
      getSbcTablesCoverage()
        .then(setSbcCoverage)
        .catch(() => {});
    };
    refresh();
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, tab]);

  if (checking) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:T.bg, color:T.mt,
      fontFamily:"'Outfit',sans-serif" }}>
      Verifying access…
    </div>
  );

  if (!isAdmin) return null;

  return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex', fontFamily:"'Outfit',sans-serif" }}>
      {/* ── Sidebar ── */}
      <CollapsibleSidebar width="224px" bg={T.s1} borderColor={T.bd}
        accentFrom={T.or} accentTo={T.or2} mutedColor={T.mt}
        position="fixed" top={0} height="100%"
        storageKey="kailux:adminSidebarCollapsed" ariaLabel="admin navigation"
        onCollapsedChange={setSidebarCollapsed}>
      <aside style={{ width:'224px', background:T.s1, borderRight:`1px solid ${T.bd}`,
        display:'flex', flexDirection:'column', padding:'16px', height:'100%' }}>
        {/* Brand */}
        <div style={{ marginBottom:'24px', padding:'0 8px', display:'flex', alignItems:'center', gap:'8px' }}>
          <p style={{ fontSize:'18px', color:T.tx, letterSpacing:'-0.02em', fontFamily:"'Syne', sans-serif", fontWeight:800, margin:0 }}>
            Kai<span style={{ color: T.or }}>Lux</span>
          </p>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px',
            fontSize:'10px', fontWeight:700, color:T.rd, background:T.rdd,
            border:`1px solid ${alpha(T.rd, 0.3)}`, borderRadius:'999px',
            padding:'3px 8px 3px 6px', textTransform:'uppercase', letterSpacing:'0.06em', lineHeight:1 }}>
            <ShieldCheck size={11} strokeWidth={2.5} />
            Admin
          </span>
        </div>

        {/* Nav */}
        <nav style={{ display:'flex', flexDirection:'column', gap:'4px', flex:1, minHeight:0, overflowY:'auto' }}>
          {TABS.map(t => (
            <NavItem
              key={t.key}
              icon={t.icon}
              label={t.label}
              badge={t.key === 'escalations' ? unreadCount : t.key === 'practice-numbers' ? pnPendingCount : t.key === 'statutory-rates' ? srPendingCount : t.key === 'sbc-tables' ? (sbcCoverage && sbcCoverage.level !== 'ok' ? 1 : 0) : 0}
              active={tab === t.key}
              onClick={() => setTab(t.key)}
            />
          ))}
        </nav>

        {/* Footer — signed-in-as card + working sign out, matching the
            client app's sidebar pattern (Dashboard.jsx) instead of a bare
            line of unboxed email text with no way to log out. */}
        <div style={{ borderTop:`1px solid ${T.bd}`, paddingTop:'12px', marginTop:'16px' }}>
          {/* "Open Client App" — replaces a dead button here (30 Jul 2026).
              The old version called navigate('/dashboard'), which only works
              if AdminBoard is mounted inside the client app's own router.
              Since the admin/client split moved to separate subdomains
              (admin.kailux.co.za vs app.kailux.co.za — see src/main.jsx and
              src/AdminApp.jsx), AdminBoard is now ONLY ever rendered inside
              AdminApp.jsx on the admin subdomain, so isAdminHost is always
              true here in production and that button's `!isAdminHost`
              branch could never actually render. This is the real fix: a
              genuine cross-origin link, opened in a new tab so the Admin
              Board session/place stays intact in this tab.

              UPDATE (30 Aug 2026): this used to be a bare link to
              /dashboard with no session — because Supabase's session lives
              in localStorage, scoped per-origin, that dropped you on
              app.kailux.co.za logged OUT, needing a second manual login.
              Now it mints a short-lived (60s), single-use SSO token first
              (/api/admin/sso/mint) and opens /sso?token=... instead, which
              the client app's public SsoLogin.jsx exchanges for a real
              session via Supabase's verifyOtp() flow — see
              backend/main.py's "Admin -> client-app SSO" block for the
              full round trip. The token deliberately never carries a raw
              Supabase credential itself, only our own short-lived opaque
              one, to keep exposure in the URL/browser-history minimal.
              Once signed in, the maintenance-mode and feature-flag admin
              bypasses (see backend/main.py's jwt_middleware /
              require_feature_flag) mean nothing there is switched off for
              you regardless of what's flagged off or under maintenance for
              regular firms. */}
          <button
            onClick={handleOpenClientApp}
            disabled={ssoLoading}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px',
              borderRadius:'12px', fontSize:'14px', color:T.lt, background:'transparent', border:'none',
              cursor: ssoLoading ? 'not-allowed' : 'pointer', textAlign:'left', boxSizing:'border-box' }}
          >
            <span>↗</span> {ssoLoading ? 'Opening…' : 'Open Client App'}
          </button>
          {ssoError && (
            <p style={{ fontSize:'11px', color:T.rd, padding:'0 16px', margin:'0 0 6px' }}>{ssoError}</p>
          )}
          {!isAdminHost && (
            <button
              onClick={() => navigate('/dashboard')}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px',
                borderRadius:'12px', fontSize:'14px', color:T.lt, background:'transparent', border:'none', cursor:'pointer' }}
            >
              <span>←</span> Back to App
            </button>
          )}

          <button
            onClick={() => setTab('account')}
            title="My Account"
            style={{ width:'100%', display:'flex', alignItems:'center', gap:'8px', margin:'4px 0 10px',
              padding:'8px 10px', borderRadius:'9px', background: tab === 'account' ? T.ord : T.s2,
              border:`1px solid ${tab === 'account' ? T.or : T.bd}`, cursor:'pointer', textAlign:'left',
              transition:'background 0.15s, border-color 0.15s' }}
            onMouseEnter={e => { if (tab !== 'account') e.currentTarget.style.background = T.s3 }}
            onMouseLeave={e => { if (tab !== 'account') e.currentTarget.style.background = T.s2 }}
          >
            <div style={{ width:'26px', height:'26px', flexShrink:0, borderRadius:'7px', background:T.bg,
              border:`1px solid ${T.bd}`, display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:'12px', color:T.or }}>
              {(user?.user_metadata?.full_name || user?.email || 'A').trim().charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontSize:'8px', fontWeight:700, color:T.mt, letterSpacing:'0.06em',
                textTransform:'uppercase', lineHeight:1 }}>
                Signed in as
              </div>
              <div style={{ fontSize:'12.5px', fontWeight:600, color:T.tx, marginTop:'3px',
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {user?.user_metadata?.full_name || user?.email}
              </div>
            </div>
          </button>

          <button onClick={logout}
            style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
              padding:'8px', borderRadius:'8px', border:`1px solid ${alpha(T.rd, 0.3)}`,
              background:T.rdd, color:T.rd, fontSize:'13px', fontWeight:600,
              cursor:'pointer', fontFamily:"'Outfit',sans-serif", transition:'background 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background=alpha(T.rd, 0.16) }}
            onMouseLeave={e => { e.currentTarget.style.background=T.rdd }}>
            <LogOut size={14} strokeWidth={2} /> Sign Out
          </button>
        </div>
      </aside>
      </CollapsibleSidebar>

      {/* ── Main content ── */}
      <main style={{ marginLeft: sidebarCollapsed ? '0px' : '224px', flex:1,
        transition:'margin-left 0.22s cubic-bezier(0.4,0,0.2,1)' }}>
        {/* Top bar — mirrors the client app's Dashboard.jsx header so the
            Admin Board doesn't read as a bolted-on afterthought. */}
        <div style={{ padding:'16px 32px', borderBottom:`1px solid ${T.bd}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          background:T.s1, position:'sticky', top:0, zIndex:20 }}>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:800, color:T.tx }}>
              {TABS.find(t => t.key === tab)?.label || 'Overview'}
            </div>
            <div style={{ fontSize:'12.5px', fontWeight:'700', color:T.or,
              display:'flex', alignItems:'center', gap:'6px', marginTop:'3px', letterSpacing:'0.2px' }}>
              <div style={{ width:'5px', height:'5px', borderRadius:'50%',
                background:T.or, boxShadow:`0 0 6px ${T.or}` }}/>
              {new Date().toLocaleDateString('en-ZA', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px',
            padding:'6px 12px 6px 6px', borderRadius:'999px',
            background:T.s2, border:`1px solid ${T.bd}` }}>
            <div style={{ width:'26px', height:'26px', borderRadius:'50%',
              background:T.prg,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:"'Syne',sans-serif", fontWeight:'800', color:T.onpri, fontSize:'12px', flexShrink:0 }}>
              {(user?.user_metadata?.full_name || user?.email || 'A').trim().charAt(0).toUpperCase()}
            </div>
            <div style={{ fontSize:'12.5px', fontWeight:'600', color:T.tx }}>
              {user?.user_metadata?.full_name || user?.email}
            </div>
          </div>
        </div>

        <div style={{ padding:'32px', maxWidth:'1152px', margin:'0 auto' }}>
          {tab !== 'sbc-tables' && <CoverageBanner coverage={sbcCoverage} onAdd={() => setTab('sbc-tables')} />}
          {tab === 'home'        && <AdminHome />}
          {tab === 'escalations' && <Escalations onResolved={() => setUnreadCount(c => Math.max(0, c - 1))} />}
          {tab === 'practice-numbers' && <PracticeNumberQueue onQueueChanged={() => setPnPendingCount(c => Math.max(0, c - 1))} />}
          {tab === 'statutory-rates' && <StatutoryRates onPendingChanged={() => setSrPendingCount(c => Math.max(0, c - 1))} />}
          {tab === 'sbc-tables' && <SbcTaxTables onCoverageChanged={setSbcCoverage} />}
          {tab === 'audit' && <AuditAdmin />}
          {tab === 'plans'       && <PlanManagement />}
          {tab === 'firms'       && <FirmManagement />}
          {tab === 'support'     && <SupportLog />}
          {tab === 'system'      && <SystemSettings />}
          {tab === 'status'      && <StatusMaintenance />}
          {tab === 'admins'      && <AdminUsers />}
          {tab === 'account'     && <AdminSettings />}
        </div>
      </main>
    </div>
  );
}
